<script setup lang="ts">
    import { defineProps, defineEmits, reactive, computed, inject, onMounted, watch } from 'vue';
    import BSToggleButtons from '@shared/components/BSToggleButtons.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import Modal from '@shared/components/Modal.vue';
    import Loading from '@shared/components/Loading.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    const props = defineProps<{
        serviceSubscriptionId: string;
        show: boolean;
    }>();

    const emits = defineEmits<{
        (e: 'close'): void;
        (e: 'serviceSubscriptionServiceLevelUpdated'): void;
    }>();

    const state = reactive({
        selectedServiceLevelPriceId: null as string | null,
        announceUpgrade: true as boolean,
        show: props.show,
    });

    const subscriptionFilters = reactive({
        name: 'Basic' as string,
        currency: 'SEK' as string,
        version: 'All' as string,
    });

    const subscriptionTypes = [
        { name: 'Basic', value: 'Basic' },
        { name: 'Pro', value: 'Pro' },
        { name: 'Automation', value: 'Automation' },
        { name: 'Enterprise', value: 'Enterprise' },
    ];

    const currencies = [
        { name: 'SEK', value: 'SEK' },
        { name: 'EUR', value: 'EUR' },
        { name: 'USD', value: 'USD' },
        { name: 'NOK', value: 'NOK' },
        { name: 'DKK', value: 'DKK' },
    ];

    const versions = [
        { name: 'All', value: 'All' },
        { name: 'v1', value: '1' },
        { name: 'v2', value: '2' },
        { name: 'v3', value: '3' },
        { name: 'v4', value: '4' },
    ];

    const filteredSubscriptionTierServiceLevels = computed(() => {
        return (
            subscriptionAccess.state.subscriptionTierServiceLevels?.filter((subscription) => {
                const matchesName = subscription.ServiceSubscriptionTier.Name === subscriptionFilters.name;
                const matchesCurrency = subscription.Currency.CurrencyCode === subscriptionFilters.currency;
                const matchesVersion =
                    subscriptionFilters.version == 'All' || subscription.ServiceSubscriptionTier.Version.toString() === subscriptionFilters.version;
                return matchesName && matchesCurrency && matchesVersion;
            }) || []
        );
    });

    onMounted(async () => {
        state.show = props.show;
        await get();
    });

    watch(
        () => props.show,
        async (show: boolean) => {
            console.log('show', show);
            state.show = show;
            await get();
        }
    );

    async function get() {
        if (state.show) {
            await subscriptionAccess.getSubscriptionTierServiceLevels().then(() => {
                state.selectedServiceLevelPriceId = subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.ServiceLevelPriceId;

                subscriptionFilters.name = subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.ServiceSubscriptionTier.Name;
                subscriptionFilters.currency = subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.Currency.CurrencyCode;
                subscriptionFilters.version = subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.ServiceSubscriptionTier.Version.toString();
            });
        }
    }

    function closeChangeSubscriptionModal() {
        state.show = false;
        emits('close');
    }

    async function updateServiceSubscriptionServiceLevel() {
        if (state.selectedServiceLevelPriceId) {
            await subscriptionAccess
                .updateServiceSubscriptionServiceLevel(props.serviceSubscriptionId, state.selectedServiceLevelPriceId, state.announceUpgrade)
                .then(async () => {
                    state.selectedServiceLevelPriceId = null;
                    closeChangeSubscriptionModal();

                    emits('serviceSubscriptionServiceLevelUpdated');
                });
        }
    }
</script>

<template>
    <Modal size="lg" :show="state.show" @close="closeChangeSubscriptionModal" title="Change subscription" :closeOnClickOutside="true">
        <template #content>
            <Loading
                :show="subscriptionAccess.state.updatingServiceLevel || subscriptionAccess.state.loadingSubscriptionTierServiceLevels"
                loadingStyle="progress-bar"></Loading>

            <div class="alert alert-warning alert-fixed-i">
                <p>
                    <i class="fa fa-warning" aria-hidden="true"></i>
                    <strong>OBS!</strong>
                    Om du ändrar valuta på abonnemanget, se till att ändra månadskostnad för eventuella tillägg/addons.
                </p>
            </div>

            <div>
                <h5>Filters</h5>
                <div class="clearfix">
                    <div class="pull-left m-sm-r">
                        <BSToggleButtons v-model:value="subscriptionFilters.name" :options="subscriptionTypes" groupClass="form-group" buttonClass="btn-xs" />
                    </div>
                    <div class="pull-left m-sm-r">
                        <BSToggleButtons v-model:value="subscriptionFilters.currency" :options="currencies" groupClass="form-group" buttonClass="btn-xs" />
                    </div>
                    <div class="pull-left m-sm-r">
                        <BSToggleButtons v-model:value="subscriptionFilters.version" :options="versions" groupClass="form-group" buttonClass="btn-xs" />
                    </div>
                </div>
            </div>

            <template v-if="!subscriptionAccess.state.loadingSubscriptionTierServiceLevels">
                <h5>Select subscription</h5>
                <div class="form-group">
                    <select class="form-control" v-model="state.selectedServiceLevelPriceId">
                        <option
                            v-for="subscription in filteredSubscriptionTierServiceLevels"
                            :key="subscription.ServiceLevelPriceId"
                            :value="subscription.ServiceLevelPriceId">
                            {{ subscription.Name }}
                        </option>
                    </select>
                </div>

                <div class="alert alert-info alert-fixed-i" v-if="filteredSubscriptionTierServiceLevels.length === 0">
                    <p>
                        <i class="fa fa-info-circle" aria-hidden="true"></i>
                        <strong>Oops!</strong>
                        Didn't find any matching subscriptions.
                    </p>
                </div>
            </template>

            <FormCheckable
                type="checkbox"
                class="slide rounded"
                :modelValue="state.announceUpgrade"
                :checked="state.announceUpgrade"
                label="Announce upgrade in Slack"
                switchStyle="standard"
                @update:modelValue="(newVal) => (state.announceUpgrade = newVal)" />
        </template>

        <template #button>
            <button
                type="button"
                class="btn btn-primary"
                @click="updateServiceSubscriptionServiceLevel"
                :disabled="
                    state.selectedServiceLevelPriceId == null ||
                    !filteredSubscriptionTierServiceLevels.some((subscription) => subscription.ServiceLevelPriceId === state.selectedServiceLevelPriceId) ||
                    subscriptionAccess.state.updatingServiceLevel ||
                    state.selectedServiceLevelPriceId == subscriptionAccess.state.boServiceSubscription!.ServiceLevelPrice.ServiceLevelPriceId
                ">
                Update subscription
            </button>
        </template>
    </Modal>
</template>
